import {
    PLAN_DEFINITIONS,
    PLAN_OFFERS,
    REVIEW_SOURCES,
} from '@flexbyt/shared';
import { DIRECT_INTEGRATIONS } from './integrations.js';
import { WIDGET_CONTENT, WIDGET_COUNT_WORD_CAPITALISED } from './widgets.js';

/**
 * Comparison pages, and the shape that keeps them honest.
 *
 * ---------------------------------------------------------------------------
 * Why a claim is a type
 * ---------------------------------------------------------------------------
 * Comparison content is the easiest content on a marketing site to get wrong,
 * and the most expensive: a wrong price or an invented limitation is a factual
 * error about a named company, published under our name. The usual failure is
 * not malice — it is that somebody checked a competitor's pricing page in
 * March, wrote a sentence, and nobody could tell in November that the sentence
 * had expired.
 *
 * So a claim cannot be written here as a bare string. `Claim` requires the URL
 * it came from and the date it was checked, which means an unverifiable claim
 * is a type error rather than a paragraph. Pages show when each fact was last
 * checked and link the source, so a reader can audit us and a stale fact is
 * findable by date instead of by memory.
 *
 * ---------------------------------------------------------------------------
 * What this file learned from the research
 * ---------------------------------------------------------------------------
 * The obvious pitch — "ReviewsJet imports your reviews from everywhere" — does
 * not survive contact with the competitors. Senja imports from about thirty
 * sources and Testimonial.to from a similar spread; ReviewsJet has five direct
 * ones and a Chrome extension for the rest. Breadth of import is not our
 * advantage and no page here claims it is.
 *
 * What is actually different is narrower and defensible: ReviewsJet is priced
 * for a small business that wants reviews it already has to look right on its
 * own site, it does not meter widget views, and it does not charge for the
 * layouts. The testimonial platforms are built around *collecting* — forms,
 * video, case studies — which is a different job that they do better, and the
 * pages say so.
 *
 * ---------------------------------------------------------------------------
 * What these pages may not say
 * ---------------------------------------------------------------------------
 * Not "the #1 alternative", and not "businesses prefer ReviewsJet" — which is
 * what the outgoing pages said, identically, about seven different companies.
 * There is no field for a verdict. There is `theirStrengths`, and it is
 * required, because a comparison that cannot name a single thing the other
 * product does better is an advertisement and readers know it on sight.
 */

export type Claim<T> = {
  value: T;
  /** Where this was read. A page on the competitor's own site, ideally. */
  sourceUrl: string;
  /** ISO date the source was last opened and the value confirmed. */
  verifiedOn: string;
};

/**
 * The dimensions a comparison is allowed to compare on.
 *
 * A closed set rather than free text, so every page answers the same questions
 * in the same order. Two pages that compare different things cannot be read
 * side by side, and a reader who has opened three of them is exactly the reader
 * closest to buying.
 */
export const COMPARISON_FIELDS = [
  'primary-use-case',
  'import-sources',
  'collection',
  'video',
  'management',
  'tagging',
  'display',
  'metering',
  'agency',
  'pricing',
] as const;

export type ComparisonField = (typeof COMPARISON_FIELDS)[number];

export const FIELD_LABELS: Record<ComparisonField, string> = {
  'primary-use-case': 'Primary use case',
  'import-sources': 'Review import sources',
  collection: 'Collecting new reviews',
  video: 'Video testimonials',
  management: 'Review management',
  tagging: 'Tagging',
  display: 'Display options',
  metering: 'View limits',
  agency: 'Agency suitability',
  pricing: 'Pricing',
};

/**
 * One row of a comparison table.
 *
 * `ours` is a plain string and `theirs` is a `Claim`, which is the asymmetry
 * the whole file rests on: we can assert what our own product does because the
 * repository is right here, and we cannot assert what theirs does without
 * saying where we read it and when.
 */
export type ComparisonRow = {
  field: ComparisonField;
  ours: string;
  theirs: Claim<string>;
};

export type Competitor = {
  /** The URL segment. Existing indexed slugs must be reused, not renamed. */
  slug: string;
  /** The company's own spelling of its name. */
  name: string;
  homepage: string;

  /** One sentence on what they are, written as they would recognise it. */
  summary: string;
  /** Who the comparison is for — the hero's job. */
  audience: string;

  rows: ComparisonRow[];

  /** Where ReviewsJet is genuinely the better tool. */
  strengths: string[];
  /** Where they genuinely are. At least one, or the page is an advert. */
  theirStrengths: string[];

  bestForUs: string;
  bestForThem: string;

  /** Differences in how the two are actually used day to day. */
  workflowDifferences: string[];

  faqs?: { q: string; a: string }[];
};

/*
  ---------------------------------------------------------------------------
  ReviewsJet's own numbers, read from the product rather than typed
  ---------------------------------------------------------------------------
  A comparison table full of hand-written numbers goes stale the first time a
  plan changes, and the wrong number would be on a page whose entire value is
  being trustworthy about numbers. So the ones that exist as data are derived.
*/

const DIRECT_SOURCE_NAMES = DIRECT_INTEGRATIONS.map((entry) => entry.name);
const EXTENSION_SOURCE_COUNT = REVIEW_SOURCES.length - DIRECT_INTEGRATIONS.length;
const LAYOUT_COUNT = WIDGET_CONTENT.length;

const FREE = PLAN_DEFINITIONS.free.limits;
const PRO_OFFER = PLAN_OFFERS.find((offer) => offer.id === 'professional')!;
const ADVANCED_OFFER = PLAN_OFFERS.find((offer) => offer.id === 'advanced')!;

/** "Google, Trustpilot, Yelp, Tripadvisor, Airbnb, Google Play and App Store" */
function directSourceSentence(): string {
  const names = [...DIRECT_SOURCE_NAMES];
  const last = names.pop()!;
  return `${names.join(', ')} and ${last}`;
}

const OURS = {
  useCase:
    'Take reviews a business already has and put them on its website, looking like part of the site.',
  importSources: `${DIRECT_INTEGRATIONS.length} direct sources — ${directSourceSentence()} — plus a Chrome extension that copies reviews from ${EXTENSION_SOURCE_COUNT} more sites. The two are not the same: direct sources reconnect and refresh, extension imports are a one-time copy.`,
  collection: `Hosted collection forms and email review requests. A form can also send people to Google or Trustpilot to leave the review there instead. ${FREE.forms} form and ${FREE.reviewRequests} requests a month on free.`,
  video: 'None. ReviewsJet displays text reviews, ratings and reviewer photos.',
  management:
    'One moderation queue across every source. Nothing appears on the site until it is published, and any review can be tagged. Replies are written in ReviewsJet only for reviews collected through ReviewsJet — a Google review is replied to in Google, because Google owns that thread.',
  tagging: 'Tags on any review, used to filter what a given widget shows.',
  display: `${LAYOUT_COUNT} layouts — carousel, masonry, marquee, grid, list and popup — all available on every plan including free, with unlimited widgets.`,
  metering: 'None. Widget views are not counted or capped on any plan.',
  agency:
    'One login can own several client sites, but only via the Wix or Webflow app on each. No shared dashboard, no seats, and each site bills separately.',
  pricing: `Free, then $${PRO_OFFER.monthly} and $${ADVANCED_OFFER.monthly} a month. Priced by published reviews and connected sources, not by traffic.`,
} as const;

/**
 * The date the competitor facts below were last checked against live pages.
 *
 * One constant rather than a date on each row: they were all verified in one
 * pass, and a per-row date that is really the same date is an invitation to
 * update one row and leave eleven looking fresh.
 */
const VERIFIED = '2026-08-18';

function claim(value: string, sourceUrl: string): Claim<string> {
  return { value, sourceUrl, verifiedOn: VERIFIED };
}

const SENJA_PRICING = 'https://senja.io/pricing';
const SENJA_IMPORT = 'https://support.senja.io/import-testimonials-review-sites-wbjoz';
const TESTIMONIAL_PRICING = 'https://testimonial.to/pricing';
const TESTIMONIAL_IMPORT =
  'https://help.testimonial.to/en/collections/3469064-import-from-third-parties';
const ELFSIGHT_PRICING = 'https://elfsight.com/pricing/';
const ELFSIGHT_WIDGET = 'https://elfsight.com/google-reviews-widget/';
const REVIEWSIO_PRICING = 'https://www.reviews.io/front/pricingplans';
const TRUSTMARY_PRICING = 'https://trustmary.com/pricing/';

export const COMPETITORS: Competitor[] = [
  // =========================================================================
  // Senja
  // =========================================================================
  {
    slug: 'senja-io-alternative',
    name: 'Senja',
    homepage: 'https://senja.io',
    summary:
      'A testimonial platform for collecting, managing and sharing social proof, with a strong free tier and a wide import tool.',
    audience:
      'You want social proof on your site and you are deciding whether you need a testimonial platform or just a good way to show the reviews you already have.',

    rows: [
      {
        field: 'primary-use-case',
        ours: OURS.useCase,
        theirs: claim(
          'Collect testimonials — text and video — then manage, analyse and share them, including as case studies and social clips.',
          'https://senja.io'
        ),
      },
      {
        field: 'import-sources',
        ours: OURS.importSources,
        theirs: claim(
          'Around 30 sources, including Google, Trustpilot, Yelp, Airbnb, Amazon, G2, Capterra, LinkedIn, Instagram, YouTube and the app stores. Auto-sync of imported sources is a paid feature.',
          SENJA_IMPORT
        ),
      },
      {
        field: 'collection',
        ours: OURS.collection,
        theirs: claim(
          'Collection forms with invites and tracking on the free plan; 3 forms on Starter and unlimited on Pro. Includes a "Spin the Wheel" incentive on paid plans.',
          SENJA_PRICING
        ),
      },
      {
        field: 'video',
        ours: OURS.video,
        theirs: claim(
          'Yes. Video testimonials are core — the free plan covers 15 video and text testimonials, and paid plans add HD video exports and social clips.',
          SENJA_PRICING
        ),
      },
      {
        field: 'management',
        ours: OURS.management,
        theirs: claim(
          'Testimonial management with analysis, case-study generation and thank-yous. Team access on Pro.',
          SENJA_PRICING
        ),
      },
      {
        field: 'tagging',
        ours: OURS.tagging,
        theirs: claim(
          'Testimonials can be organised and filtered into widgets and Walls of Love.',
          'https://senja.io'
        ),
      },
      {
        field: 'display',
        ours: OURS.display,
        theirs: claim(
          'Unlimited widgets and unlimited Walls of Love on the free plan, with over 20 templates. "Pro widgets" are a paid addition.',
          SENJA_PRICING
        ),
      },
      {
        field: 'metering',
        ours: OURS.metering,
        theirs: claim(
          'No widget view cap listed on the pricing page; the free plan is limited by testimonial count (15) rather than by traffic.',
          SENJA_PRICING
        ),
      },
      {
        field: 'agency',
        ours: OURS.agency,
        theirs: claim(
          'Projects and seats: Starter includes 1 project and 2 seats, Pro includes 5 projects and 5 seats, with extra projects and seats available for a monthly fee.',
          SENJA_PRICING
        ),
      },
      {
        field: 'pricing',
        ours: OURS.pricing,
        theirs: claim(
          'Free plan, then Starter at $29/month and Pro at $59/month.',
          SENJA_PRICING
        ),
      },
    ],

    strengths: [
      'Costs less at every paid step. ReviewsJet’s top plan is under half the price of Senja’s entry paid plan.',
      'Every layout, including the popup, is on the free plan — there is no "pro widget" tier.',
      'Native Wix and Webflow apps, plus a WordPress plugin with shortcodes, rather than an embed everywhere.',
      'Priced by published reviews and sources, so a page that gets popular does not change the bill.',
    ],
    theirStrengths: [
      'Far more import sources — around 30, against ReviewsJet’s 5 direct connections.',
      'Video testimonials, which ReviewsJet does not do at all.',
      'Real multi-client structure: projects and seats you can buy, where ReviewsJet has neither.',
      'Collection is a much deeper product — incentives, case studies, social clips and testimonial analysis.',
      'A more generous free tier for collecting: 15 video and text testimonials against ReviewsJet’s 5 published reviews.',
    ],

    bestForUs:
      'You already have reviews on Google, Trustpilot or Yelp, you want them on your website in a layout that matches your design, and you would rather spend $8 than $29 a month to do it.',
    bestForThem:
      'You need to collect testimonials — especially video — from customers who have not written one yet, or you manage several clients and want projects and team seats to do it in.',

    workflowDifferences: [
      'ReviewsJet starts from a listing you paste; Senja starts from a form you send.',
      'ReviewsJet’s free plan limits how many reviews you can publish; Senja’s limits how many testimonials you can hold.',
      'Senja’s auto-sync needs a paid plan, as does ReviewsJet’s — on Advanced rather than on the entry tier.',
    ],

    faqs: [
      {
        q: 'Does ReviewsJet import from as many places as Senja?',
        a: `No. Senja imports from roughly thirty sources. ReviewsJet connects directly to ${DIRECT_INTEGRATIONS.length} — ${directSourceSentence()} — and uses a Chrome extension to copy reviews from other sites, which is a one-time copy rather than a connection that refreshes.`,
      },
      {
        q: 'Can ReviewsJet collect video testimonials?',
        a: 'No. ReviewsJet displays text reviews, star ratings and reviewer photos. If video testimonials matter to you, Senja is the better tool and this page is not going to argue otherwise.',
      },
    ],
  },

  // =========================================================================
  // Testimonial.to
  // =========================================================================
  {
    slug: 'testimonials-to-alternative',
    name: 'Testimonial.to',
    homepage: 'https://testimonial.to',
    summary:
      'A testimonial collection platform built around video, with a Wall of Love for displaying what it collects.',
    audience:
      'You are choosing between collecting fresh testimonials from customers and displaying reviews you have already earned elsewhere.',

    rows: [
      {
        field: 'primary-use-case',
        ours: OURS.useCase,
        theirs: claim(
          'Collect text and video testimonials through a hosted page, then display them in a Wall of Love.',
          'https://testimonial.to'
        ),
      },
      {
        field: 'import-sources',
        ours: OURS.importSources,
        theirs: claim(
          'Imports from third parties including Google, Trustpilot, G2, Capterra, LinkedIn, X, YouTube, Instagram, Facebook and the app stores, plus a Chrome extension for other sites.',
          TESTIMONIAL_IMPORT
        ),
      },
      {
        field: 'collection',
        ours: OURS.collection,
        theirs: claim(
          'A hosted collection page is the core of the product, with text and video responses.',
          'https://testimonial.to'
        ),
      },
      {
        field: 'video',
        ours: OURS.video,
        theirs: claim(
          'Yes, and it is the main draw. The free plan covers 2 video testimonials; Ultimate and above are unlimited.',
          TESTIMONIAL_PRICING
        ),
      },
      {
        field: 'management',
        ours: OURS.management,
        theirs: claim(
          'Testimonials are organised per space, with the plan governing how many can be displayed.',
          TESTIMONIAL_PRICING
        ),
      },
      {
        field: 'tagging',
        ours: OURS.tagging,
        theirs: claim(
          'Testimonials are grouped by space; tagging is not listed as a plan feature on the pricing page.',
          TESTIMONIAL_PRICING
        ),
      },
      {
        field: 'display',
        ours: OURS.display,
        theirs: claim(
          'A public testimonial page and Wall of Love widgets, with white-label widgets and a custom domain from Starter up.',
          TESTIMONIAL_PRICING
        ),
      },
      {
        field: 'metering',
        ours: OURS.metering,
        theirs: claim(
          'No view cap listed; plans are limited by testimonial counts and by how many can be displayed.',
          TESTIMONIAL_PRICING
        ),
      },
      {
        field: 'agency',
        ours: OURS.agency,
        theirs: claim(
          'Plans include 1 space; additional spaces are charged per year on Ultimate and Ultimate +.',
          TESTIMONIAL_PRICING
        ),
      },
      {
        field: 'pricing',
        ours: OURS.pricing,
        theirs: claim(
          'Free plan (2 video, 10 text testimonials), then Starter $25/month, Ultimate $50/month and Ultimate + $95/month.',
          TESTIMONIAL_PRICING
        ),
      },
    ],

    strengths: [
      'Substantially cheaper: ReviewsJet’s top plan is under a quarter of the price of Testimonial.to’s Ultimate.',
      `Built for reviews that already exist on ${directSourceSentence()}, with connections that refresh rather than a one-time import.`,
      `${WIDGET_COUNT_WORD_CAPITALISED} display layouts rather than a Wall of Love, and unlimited widgets on every plan.`,
      'Native Wix and Webflow apps and a WordPress plugin.',
    ],
    theirStrengths: [
      'Video testimonials, unlimited on the higher plans. ReviewsJet has none.',
      'A far stronger collection flow — the hosted collection page is the product, and it is good at it.',
      'White-label widgets and a custom domain from the entry paid plan, which ReviewsJet does not offer at any price.',
      'A recognised Wall of Love format that many buyers are specifically looking for.',
    ],

    bestForUs:
      'Your reviews are already on Google or Trustpilot, you want them on your own site in a layout that fits the design, and video is not part of the plan.',
    bestForThem:
      'You want customers to record video testimonials, or you need white-label widgets on your own domain.',

    workflowDifferences: [
      'Testimonial.to sends a link and waits for a response; ReviewsJet reads a listing you already have.',
      'Testimonial.to organises work into spaces; ReviewsJet organises it into one workspace per website.',
      'ReviewsJet’s collection form can hand the customer off to Google or Trustpilot instead of keeping the review — useful when the public rating matters more than the testimonial.',
    ],

    faqs: [
      {
        q: 'Is ReviewsJet a replacement for a Wall of Love?',
        a: 'For displaying reviews, the masonry layout does the same job. For collecting the video testimonials that usually fill one, no — ReviewsJet does not record or display video.',
      },
    ],
  },

  // =========================================================================
  // Elfsight
  // =========================================================================
  {
    slug: 'elfsight-alternative',
    name: 'Elfsight',
    homepage: 'https://elfsight.com',
    summary:
      'A large library of embeddable website widgets, of which reviews is one category, priced by how often the widgets load.',
    audience:
      'You want reviews on your site and you are weighing a dedicated review tool against a general widget suite you may already be paying for.',

    rows: [
      {
        field: 'primary-use-case',
        ours: OURS.useCase,
        theirs: claim(
          'Add widgets of many kinds to a website — forms, chats, galleries, sliders and reviews — from one library.',
          ELFSIGHT_PRICING
        ),
      },
      {
        field: 'import-sources',
        ours: OURS.importSources,
        theirs: claim(
          'Per-source widgets for Google, Facebook, Yelp, Tripadvisor, Airbnb, Booking.com, Etsy, eBay and the app stores, plus an all-in-one reviews widget covering 20+ platforms.',
          ELFSIGHT_WIDGET
        ),
      },
      {
        field: 'collection',
        ours: OURS.collection,
        theirs: claim(
          'Not a review-collection product; the review widgets display reviews held on the source platform.',
          ELFSIGHT_WIDGET
        ),
      },
      {
        field: 'video',
        ours: OURS.video,
        theirs: claim(
          'Not part of the review widgets, though the wider app library includes video and gallery apps.',
          ELFSIGHT_WIDGET
        ),
      },
      {
        field: 'management',
        ours: OURS.management,
        theirs: claim(
          'Widget-level filtering — for example showing only positive ratings — rather than a moderation queue that holds reviews back from publication.',
          ELFSIGHT_WIDGET
        ),
      },
      {
        field: 'tagging',
        ours: OURS.tagging,
        theirs: claim(
          'Not offered for reviews; filtering is by rating and source.',
          ELFSIGHT_WIDGET
        ),
      },
      {
        field: 'display',
        ours: OURS.display,
        theirs: claim(
          '6 layout variations for the Google Reviews widget, with colour, font and text-size customisation and an AI review summary.',
          ELFSIGHT_WIDGET
        ),
      },
      {
        field: 'metering',
        ours: OURS.metering,
        theirs: claim(
          'Yes — plans are metered by widget views. The free plan allows 200 views a month and 1 widget, with Elfsight branding shown.',
          ELFSIGHT_PRICING
        ),
      },
      {
        field: 'agency',
        ours: OURS.agency,
        theirs: claim(
          'Projects and collaborators are included, and the free plan allows unlimited websites; higher view allowances come with higher tiers.',
          ELFSIGHT_PRICING
        ),
      },
      {
        field: 'pricing',
        ours: OURS.pricing,
        theirs: claim(
          'Free plan, then single-app plans from $4 to $16/month and Enterprise tiers above that; the 96-app pack runs from $12 to $48/month with higher Enterprise tiers. Prices shown are discounted against a higher regular rate.',
          ELFSIGHT_PRICING
        ),
      },
    ],

    strengths: [
      'No view metering. A widget on a busy page costs the same as one on a quiet page, where Elfsight’s plans are sized by monthly views.',
      'A real moderation queue — reviews are held until published, rather than filtered at display time.',
      'Reviews from several sources land in one place with one rating summary, instead of being separate widgets.',
      'Unlimited widgets on every plan, including free.',
      'Tagging, replies and review requests, none of which a display widget does.',
    ],
    theirStrengths: [
      'Many more review sources have a dedicated widget — Booking.com, Etsy, eBay and others ReviewsJet has no direct connection for.',
      'The single-app entry price is lower than ReviewsJet’s at $4/month if reviews are all you need and your traffic is modest.',
      'If you need widgets beyond reviews — forms, chat, galleries — the 96-app pack covers them from one subscription and one vendor.',
      'The free plan allows unlimited websites, where ReviewsJet’s free plan is one workspace per site.',
    ],

    bestForUs:
      'Reviews are the point rather than one widget among many, you want to moderate what appears, and you do not want the bill to follow your traffic.',
    bestForThem:
      'You need a spread of unrelated widgets on the site, or your traffic is low enough that a metered plan is genuinely cheaper.',

    workflowDifferences: [
      'Elfsight configures a widget against a source; ReviewsJet imports reviews into an account you then manage.',
      'Elfsight filters at display time; ReviewsJet publishes deliberately, so an unpublished review is not on the site at all.',
      'Elfsight’s cost moves with pageviews; ReviewsJet’s moves with how many reviews you publish.',
    ],

    faqs: [
      {
        q: 'Does ReviewsJet limit how many times a widget can load?',
        a: 'No. There is no view counter on any plan. Plans are limited by how many reviews you publish and how many sources you connect.',
      },
      {
        q: 'Can I show reviews from a source ReviewsJet does not connect to directly?',
        a: 'Often yes, using the Chrome extension to copy them in — but that is a one-time copy, not a connection that refreshes. If a live connection to that specific source is essential, check whether Elfsight has a dedicated widget for it.',
      },
    ],
  },

  // =========================================================================
  // REVIEWS.io
  // =========================================================================
  {
    slug: 'reviews-io-alternative',
    name: 'REVIEWS.io',
    homepage: 'https://www.reviews.io',
    summary:
      'An ecommerce review platform for collecting company and product reviews at volume, with Google Seller Ratings and rich snippets.',
    audience:
      'You are looking at REVIEWS.io and wondering whether you need a full review-collection platform or a way to display the reviews you already have.',

    rows: [
      {
        field: 'primary-use-case',
        ours: OURS.useCase,
        theirs: claim(
          'Collect company and product reviews from customers after purchase, at volume, and feed them to Google as seller ratings and rich snippets.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'import-sources',
        ours: OURS.importSources,
        theirs: claim(
          'Third-party review collection is included from the Start-Up tier upward; the platform is built primarily around reviews it collects itself.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'collection',
        ours: OURS.collection,
        theirs: claim(
          'The core of the product, and priced by it: plans are sized by monthly review invites, from 300 on Essentials to 10,000 on Plus.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'video',
        ours: OURS.video,
        theirs: claim(
          'Rich media and user-generated content are part of the platform on higher tiers.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'management',
        ours: OURS.management,
        theirs: claim(
          'Full review management across company and product reviews, with automated collection flows.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'tagging',
        ours: OURS.tagging,
        theirs: claim(
          'Product-level attribution and segmentation are part of the platform; specific tagging features are not itemised on the pricing page.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'display',
        ours: OURS.display,
        theirs: claim(
          '15+ on-site display widgets on the entry plan, with more customisation on higher tiers.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'metering',
        ours: OURS.metering,
        theirs: claim(
          'Not metered by views — metered by review invites sent per month.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'agency',
        ours: OURS.agency,
        theirs: claim(
          'Aimed at brands rather than agencies; pricing is per account and scales with invite volume.',
          REVIEWSIO_PRICING
        ),
      },
      {
        field: 'pricing',
        ours: OURS.pricing,
        theirs: claim(
          'A free tier with 25 review invites, then four paid plans — Essentials, Start-Up, Grow and Plus — rising to roughly ten thousand invites a month. Displayed prices vary by region and currency, so check the live page for your own.',
          REVIEWSIO_PRICING
        ),
      },
    ],

    strengths: [
      'Far cheaper for a small business that is not collecting reviews at volume.',
      'Set up in an afternoon without an ecommerce platform, an order feed or an onboarding call.',
      `${WIDGET_COUNT_WORD_CAPITALISED} display layouts on every plan with no view limits and no branding on paid plans.`,
      'Works on a plain marketing site — Wix, Webflow, WordPress, Squarespace — rather than assuming a store.',
    ],
    theirStrengths: [
      'Google Seller Ratings, which ReviewsJet does not provide and which is the reason many ecommerce brands buy a review platform at all.',
      'Review collection at genuine volume, with automated post-purchase flows sized in thousands of invites a month.',
      'Product-level reviews across a catalogue, where ReviewsJet’s product widget is limited to Wix.',
      'A far more complete ecommerce feature set, including user-generated content on higher tiers.',
      'An established, licensed review platform whose collected reviews carry more weight than imported ones.',
    ],

    bestForUs:
      'You are a small business or a service company whose reviews already live on Google or Trustpilot, and you want them displayed well without buying a collection platform.',
    bestForThem:
      'You run an ecommerce store, you need Google Seller Ratings and product reviews across a catalogue, and you are collecting reviews from orders at volume.',

    workflowDifferences: [
      'REVIEWS.io is wired into orders and sends invites; ReviewsJet reads listings that already exist.',
      'REVIEWS.io’s cost scales with how many customers you ask; ReviewsJet’s scales with how many reviews you publish.',
      'REVIEWS.io owns the reviews it collects as a licensed platform; reviews ReviewsJet imports stay on Google or Trustpilot and are displayed from a copy.',
    ],

    faqs: [
      {
        q: 'Can ReviewsJet give me Google Seller Ratings?',
        a: 'No. Seller Ratings come from a Google-licensed review partner, which ReviewsJet is not. If Seller Ratings are the goal, REVIEWS.io is the right kind of product and ReviewsJet is not a substitute.',
      },
      {
        q: 'Does ReviewsJet do product reviews?',
        a: 'There is a product reviews widget, but it is available on Wix only. For product reviews across a catalogue on other platforms, a dedicated ecommerce review platform is the better fit.',
      },
    ],
  },

  // =========================================================================
  // Trustmary
  // =========================================================================
  {
    slug: 'trustmary-alternative',
    name: 'Trustmary',
    homepage: 'https://trustmary.com',
    summary:
      'A review and feedback platform combining survey-based collection, review import and website widgets.',
    audience:
      'You want reviews on your site and are weighing a combined survey-and-review platform against a tool that only does import, manage and display.',

    rows: [
      {
        field: 'primary-use-case',
        ours: OURS.useCase,
        theirs: claim(
          'Collect, analyse and showcase customer feedback — surveys and reviews together, then displayed on the site.',
          TRUSTMARY_PRICING
        ),
      },
      {
        field: 'import-sources',
        ours: OURS.importSources,
        theirs: claim(
          'Review sources can be connected and displayed; the pricing page counts "sources" as one of the metrics plans are measured on.',
          TRUSTMARY_PRICING
        ),
      },
      {
        field: 'collection',
        ours: OURS.collection,
        theirs: claim(
          'Survey-based collection is central — the plan metrics include survey responses received and survey emails sent.',
          TRUSTMARY_PRICING
        ),
      },
      {
        field: 'video',
        ours: OURS.video,
        theirs: claim(
          'Video testimonials are part of the platform.',
          'https://trustmary.com/key-features/'
        ),
      },
      {
        field: 'management',
        ours: OURS.management,
        theirs: claim(
          'Feedback and reviews are collected and analysed together, with survey analytics alongside review display.',
          TRUSTMARY_PRICING
        ),
      },
      {
        field: 'tagging',
        ours: OURS.tagging,
        theirs: claim(
          'Not itemised on the pricing page.',
          TRUSTMARY_PRICING
        ),
      },
      {
        field: 'display',
        ours: OURS.display,
        theirs: claim(
          'Website widgets for showing collected reviews and testimonials.',
          'https://trustmary.com/key-features/'
        ),
      },
      {
        field: 'metering',
        ours: OURS.metering,
        theirs: claim(
          'Yes — "views" is one of the metrics plans are measured on, alongside sources, responses and requests.',
          TRUSTMARY_PRICING
        ),
      },
      {
        field: 'agency',
        ours: OURS.agency,
        theirs: claim(
          'Not described as an agency product on the pricing page.',
          TRUSTMARY_PRICING
        ),
      },
      {
        field: 'pricing',
        ours: OURS.pricing,
        theirs: claim(
          'A free plan is offered. The pricing page does not publish a fixed table of tiers and prices — it directs you to sign up free or talk to them — so we are not quoting figures we cannot verify.',
          TRUSTMARY_PRICING
        ),
      },
    ],

    strengths: [
      'Published, fixed prices you can read before signing up.',
      'No view metering, where Trustmary counts views as a plan metric.',
      'A narrower job done simply: import, moderate, display, with no survey layer to configure.',
      'Native Wix and Webflow apps and a WordPress plugin.',
    ],
    theirStrengths: [
      'Survey-based collection and feedback analytics, which ReviewsJet does not have in any form.',
      'Video testimonials, which ReviewsJet does not support.',
      'A single platform for measuring satisfaction and displaying the results, where ReviewsJet only does the second half.',
      'Usage-based plans can be shaped to an unusual mix of needs, where ReviewsJet has three fixed tiers.',
    ],

    bestForUs:
      'You want reviews you already have on your website quickly, at a price you can see up front, and you have no need to run surveys.',
    bestForThem:
      'You want to measure customer satisfaction with surveys and turn the results into testimonials, or you need video.',

    workflowDifferences: [
      'Trustmary starts with a survey; ReviewsJet starts with a listing that already has reviews on it.',
      'Trustmary counts widget views as part of the plan; ReviewsJet does not count them at all.',
      'ReviewsJet’s prices are on its pricing page; Trustmary’s are established by signing up or asking.',
    ],
  },
];

/**
 * Competitors with an indexed page that this rebuild does not carry forward.
 *
 * ---------------------------------------------------------------------------
 * Why these are listed rather than rebuilt
 * ---------------------------------------------------------------------------
 * The outgoing site has seven comparison pages. Three are rewritten above and
 * two more are new. These four are not, and the reason is not that they were
 * forgotten — it is the brief's instruction not to write a comparison merely
 * because a competitor exists, plus a specific problem with two of them.
 *
 * Yotpo and Fera are Shopify-first products. A visitor searching "Fera
 * alternative" is, in the overwhelming majority of cases, a Shopify merchant —
 * and ReviewsJet no longer supports Shopify, so the honest answer to that
 * search is "not this". Rewriting the page to say so is possible; ranking for
 * a query we cannot serve is the thing worth avoiding.
 *
 * These URLs are indexed, so they cannot simply 404. What happens to them is a
 * decision recorded in `lib/legacy-urls.ts`, not one this file makes.
 */
export const RETIRED_COMPARISONS = [
  {
    slug: 'yotpo-alternative',
    name: 'Yotpo',
    why: 'Shopify-first ecommerce platform. ReviewsJet no longer supports Shopify, so the search intent cannot be served.',
  },
  {
    slug: 'fera-reviews-alternative',
    name: 'Fera Reviews',
    why: 'Shopify-first ecommerce reviews app. The people searching for an alternative are Shopify merchants, and ReviewsJet no longer supports Shopify.',
  },
  {
    slug: 'kudobuzz-reviews-alternative',
    name: 'Kudobuzz',
    why: 'Little current public product information; a comparison would be guesswork.',
  },
  {
    slug: 'feta-ai-alternative',
    name: 'Feta',
    why: 'Not a review display product in the sense this site compares against.',
  },
] as const;

export function competitorBySlug(slug: string): Competitor | undefined {
  return COMPETITORS.find((entry) => entry.slug === slug);
}

export function comparisonPath(competitor: Competitor): string {
  return `/compare/${competitor.slug}`;
}

/** Every dated claim on a page, for the freshness line and the staleness test. */
export function claimsFor(competitor: Competitor): Claim<string>[] {
  return competitor.rows.map((row) => row.theirs);
}

/** The oldest verification date on the page — what "checked on" should show. */
export function oldestVerification(competitor: Competitor): string {
  return claimsFor(competitor)
    .map((entry) => entry.verifiedOn)
    .sort()[0]!;
}

/** Has every fact on this page been checked since the given date? */
export function claimsVerifiedSince(
  competitor: Competitor,
  since: Date
): boolean {
  return claimsFor(competitor).every(
    (entry) => new Date(entry.verifiedOn) >= since
  );
}
